import { useEffect, useRef } from 'react'
import { DISPLAY_BALANCE_ON_LOAD } from '@/constants/env'
import { sagaActions } from '@/redux/sagaActions'
import { useNavigate } from 'react-router'
import { type LayoutProps } from '@/ui-components/layouts'
import { RootState } from '@/redux/reducers'
import { useDispatch, useSelector } from 'react-redux'
import useLanguageChange from './useLanguageChange'
import useFooterBar from './useFooterBar'
import { useTheme } from '@/ui-components/context/ThemeProvider'
import { FLAG_ASSETS } from '@/ui-components/constants/asset'
import { useThemeAssets } from '@/ui-components/hooks/useThemeAssets'
import { useRefreshBalance } from './useRefreshBalance'
import { type Language as LanguagesWithFlagType } from '@/ui-components/components/LanguageDropdown/LanguageDropdown.types'
import { getLanguageFromStorage } from '@/i18n/storage'
import {
  getThemeFromStorage,
  getThemeMode,
} from '@/ui-components/utils/theme-utils'
import { ThemeMode } from '@/ui-components/constants/theme'
import {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
  Language,
} from '@/i18n/constants'
import { parseLanguage } from '@/i18n/utils'

type UseLayoutReturn = Omit<LayoutProps, 'children'>
type UseLayout = () => UseLayoutReturn

const currencyFormat = (num?: number | bigint) => {
  if (!num) return '0'

  // TODO: Change currency follow setting from provider
  return new Intl.NumberFormat('en-US', {
    style: 'decimal', // TODO: change to currency
    // currency: 'THB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    // currencyDisplay: 'symbol',
  }).format(num)
}

/**
 * Mapping of languages to their flags
 */
const LANGUAGES_FLAGS_MAPPING: Record<Language, string> = {
  [Language.TH]: FLAG_ASSETS.th,
  [Language.EN]: FLAG_ASSETS.en,
  [Language.MY]: FLAG_ASSETS.mm,
  [Language.KM]: FLAG_ASSETS.km,
}

const DEFAULT_LANGUAGES_WITH_FLAG: LanguagesWithFlagType[] =
  AVAILABLE_LANGUAGES.map((lang) => ({
    code: lang,
    label: lang.toUpperCase(),
    flag: LANGUAGES_FLAGS_MAPPING[lang],
  }))

const useLayout: UseLayout = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { theme, setTheme } = useTheme()
  const { svgAssets } = useThemeAssets()
  const hasInitializedLanguage = useRef(false)
  const hasInitializedTheme = useRef(false)

  const logoUrl = useSelector((state: RootState) => {
    return state.user?.logoUrl
  })
  const logoUrlDark = useSelector((state: RootState) => {
    return state.user?.logoUrlDark
  })
  const logoLinkUrl = useSelector((state: RootState) => {
    return state.user?.logoLinkUrl
  })
  const userName = useSelector((state: RootState) => {
    return state.user?.userName
  })
  const userBalance = useSelector((state: RootState) => {
    return state.user?.userBalance
  })
  const provider = useSelector((state: RootState) => {
    return state.user?.provider
  })
  const playerTheme = useSelector((state: RootState) => {
    return state.user?.theme
  })
  const { selectedLang, changeLanguage } = useLanguageChange()
  const { navItems, activePath, onNavClick } = useFooterBar()
  const { isUpdating, onRefreshClick, cooldown, isDisabled } =
    useRefreshBalance()

  // Feature flag: control balance fetch/display on load
  const shouldDisplayBalanceOnLoad = DISPLAY_BALANCE_ON_LOAD

  useEffect(() => {
    if (shouldDisplayBalanceOnLoad) {
      dispatch({
        type: sagaActions.USER_FETCH_BALANCE,
      })
    }
  }, [dispatch, shouldDisplayBalanceOnLoad])

  // Set default language from provider only once when provider is available and no user choice exists
  useEffect(() => {
    const lang = getLanguageFromStorage()
    if (provider?.defaultLanguage && !hasInitializedLanguage.current && !lang) {
      hasInitializedLanguage.current = true
      changeLanguage(provider.defaultLanguage)
    }
  }, [provider?.defaultLanguage, changeLanguage])

  // Set theme from playerToken only once when theme is available and no user choice exists
  useEffect(() => {
    const theme = getThemeFromStorage()
    if (playerTheme && !hasInitializedTheme.current && !theme) {
      hasInitializedTheme.current = true
      setTheme(playerTheme)
    }
  }, [playerTheme, setTheme])

  // Get available languages from provider or use default
  const availableLanguages: LanguagesWithFlagType[] =
    provider?.languages?.map(
      (lang: string | { code: string; label?: string; flag?: string }) => {
        if (typeof lang === 'string') {
          const langCode = parseLanguage(lang)
          return {
            code: lang,
            label: lang.toUpperCase(),
            flag:
              LANGUAGES_FLAGS_MAPPING[langCode] ||
              LANGUAGES_FLAGS_MAPPING[DEFAULT_LANGUAGE],
          }
        }

        const langCode = parseLanguage(lang.code)

        return {
          code: lang.code,
          label: lang.label || lang.code.toUpperCase(),
          flag:
            lang.flag ||
            LANGUAGES_FLAGS_MAPPING[langCode] ||
            LANGUAGES_FLAGS_MAPPING[DEFAULT_LANGUAGE],
        }
      },
    ) || DEFAULT_LANGUAGES_WITH_FLAG

  // Determine masking behavior and display balance
  const isMaskedBalance =
    !shouldDisplayBalanceOnLoad && userBalance === undefined
  const displayBalance =
    shouldDisplayBalanceOnLoad || userBalance !== undefined
      ? currencyFormat(userBalance)
      : undefined

  // Get theme mode for logo selection
  const themeMode = getThemeMode(theme)

  // Get default logo based on theme mode (using dynamic assets from useThemeAssets)
  let preLogo = svgAssets.LOGO[themeMode]

  // Override with custom logo if provided
  if (themeMode === ThemeMode.DARK) {
    if (logoUrlDark && logoUrlDark !== '') {
      preLogo = logoUrlDark
    } else if (logoUrl && logoUrl !== '') {
      preLogo = logoUrl
    }
  }
  if (themeMode === ThemeMode.LIGHT) {
    if (logoUrl && logoUrl !== '') {
      preLogo = logoUrl
    } else if (logoUrlDark && logoUrlDark !== '') {
      preLogo = logoUrlDark
    }
  }

  const handleLogoClick = () => {
    if (logoLinkUrl && logoLinkUrl !== '') {
      window.location.assign(logoLinkUrl)
      return
    }
    navigate('/lotto')
  }

  return {
    username: userName,
    balance: displayBalance,
    isMaskedBalance,
    onLogoClick: handleLogoClick,
    onLanguageChange: (code: string) => {
      changeLanguage(code)
    },
    currentLang: selectedLang || provider?.defaultLanguage || DEFAULT_LANGUAGE,
    languages: availableLanguages,
    logo: preLogo,
    navItems,
    activePath,
    onNavClick,
    isUpdating,
    onRefreshClick,
    cooldown,
    isDisabled,
  }
}

export default useLayout
